import type { ComponentProps } from 'react';
import Link from 'next/link';

import { TeamAvatar, TeamUserAvatar } from '@poseidon/components/Avatar';
import { cn } from '@poseidon/components/cn';
import { Tooltip } from '@poseidon/components/Input2';

import NotificationLabel from '~/components/NotificationLabel';
import { dataOnboardingIds } from '~/components/Onboarding/onboarding.model';

/** Something in Settings needs attention (e.g. CRM records to match): a red count beside the user row. */
export type UserRowAlert = {
  count: number;
  href: string;
  tooltip: string;
  /** Plays the nav-badge shake; owned by the container like every other nav badge. */
  isShaking?: boolean;
};

interface UserLogoutRowProps {
  authData: NonNullable<ComponentProps<typeof TeamUserAvatar>['userTeam']> & {
    teamName: string;
    teamLogo: ComponentProps<typeof TeamAvatar>['logo'];
  };
  minimized?: boolean;
  alert?: UserRowAlert;
}

// Pure: renders from props only. The tooltip's anchor/hover state lives inside
// the design-system `Tooltip` leaf (rendered here `as={Link}`) rather than in a
// hook in this component's body.
export const UserLogoutRow = ({ authData, minimized, alert }: UserLogoutRowProps) => {
  const name = authData.userTeamName ?? authData.userEmail;
  const tooltip = minimized ? `${name} (${authData.teamName})` : undefined;
  const showAlert = alert != null && alert.count > 0;

  return (
    <div className="relative whitespace-pre flex flex-row items-center gap-1 overflow-hidden pt-6">
      <Tooltip
        as={Link}
        tooltip={tooltip}
        href={`/teams/active/user`}
        data-onboarding={dataOnboardingIds.settingsTrigger}
        className={cn(
          'relative flex shrink grow basis-0 cursor-pointer flex-row gap-3 rounded-md p-2 transition-colors hover:bg-gray-100 dark:hover:bg-slate-900',
          showAlert && !minimized && 'pr-12',
        )}>
        <TeamUserAvatar className="size-10" shape="circle" userTeam={authData} />

        <TeamAvatar
          className="absolute bottom-1 left-1 size-5"
          logo={authData.teamLogo}
          name={authData.teamName}
          shape="circle"
        />
        <div className="flex w-0 shrink grow basis-0 flex-col overflow-hidden text-ellipsis">
          <div className="max-h-5 overflow-hidden text-ellipsis text-sm font-semibold text-gray-700 dark:text-white">
            {name}
          </div>
          <div className="max-h-5 overflow-hidden text-ellipsis text-sm font-normal text-gray-500 dark:text-gray-400">
            {authData.teamName}
          </div>
        </div>
      </Tooltip>
      {/* A sibling link, not nested in the user link: it goes straight to what needs attention. Collapsed
          nav: a corner badge on the avatar; expanded: the row's right edge, like the nav-row counters. */}
      {alert != null && alert.count > 0 && (
        <Tooltip
          as={Link}
          tooltip={alert.tooltip}
          href={alert.href}
          aria-label={alert.tooltip}
          data-testid="user-row-alert"
          className={cn(
            'absolute rounded-full transition-[filter] hover:brightness-110',
            minimized && 'top-5 left-8',
            !minimized && 'top-[calc(50%+12px)] right-2 -translate-y-1/2',
          )}>
          <NotificationLabel
            className="static translate-y-0 ring-2 ring-(--nav-bg-color)"
            counterValue={alert.count}
            isShaking={alert.isShaking}
          />
        </Tooltip>
      )}
    </div>
  );
};
